import { useIsNewSessionListEnabled } from '@/features/flags';
import {
  ability,
  ACTIONS,
  hasUpdatedAbilityAtom,
  PERMISSION_SUBJECTS,
} from '@/helpers/casl/ability';
import { useCanViewOthersSessions } from '@/hooks/useCanViewOthersSessions';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

export type NewSessionDelegation = 'none' | 'assistantPicker' | 'adminSubmenu';

/**
 * The "create a session on behalf of another clinician" delegation mode for
 * the current user.
 *
 * Single source of truth for the entry points that offer delegation — the
 * sidebar new-session button, the session-list toolbar CTA, the
 * schedule-upload modal, and the empty-state CTA — so the rule can't drift
 * between them.
 *
 * - **Assistants** always get `'assistantPicker'` (unchanged behaviour) —
 *   checked first so an assistant who also holds `TEAM_OTHERS_SESSION_CREATE`
 *   never falls into the admin branch below.
 * - **Admins** who can both create sessions for others
 *   (`TEAM_OTHERS_SESSION_CREATE` — mirrors the backend endpoint guard) and
 *   view other members' sessions (support-role bypass, or the team's
 *   session-sharing allowlist) get `'adminSubmenu'` — every "New session"
 *   entry point self-creates directly, and the toolbar CTA's chevron gains a
 *   "New session for" submenu as the sole create-for-others path.
 *   An admin who lacks self `SESSION_CREATE` would 400 on that direct
 *   self-create, so they fall back to `'assistantPicker'` instead — the same
 *   full picker an assistant gets.
 * - Everyone else gets `'none'`.
 *
 * Deliberately role-scoped rather than permission-only: the base Clinician role
 * also holds `TEAM_OTHERS_SESSION_CREATE`, so gating on the permission alone
 * would expose delegation to every clinician in a team.
 *
 * Gated on `isNewSessionListEnabled` here so callers don't each repeat it (and
 * can't drift). Surfaces that only ever render inside the new sessions
 * experience — the toolbar CTA and the empty state — pass through unchanged.
 */
export const useNewSessionDelegation = (): NewSessionDelegation => {
  const hasUpdatedAbility = useAtomValue(hasUpdatedAbilityAtom);
  const { isTeamAssistant, isTeamAdmin } = useCombinedQueriesTeamGeneralInfo();
  const canViewOthersSessions = useCanViewOthersSessions();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();

  return useMemo<NewSessionDelegation>(() => {
    if (!isNewSessionListEnabled) {
      return 'none';
    }

    if (isTeamAssistant) {
      return 'assistantPicker';
    }

    const canCreateForOthers =
      hasUpdatedAbility &&
      ability.can(ACTIONS.ACCESS, PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_CREATE);

    if (!(isTeamAdmin && canCreateForOthers && canViewOthersSessions)) {
      return 'none';
    }

    return ability.can(ACTIONS.ACCESS, PERMISSION_SUBJECTS.SESSION_CREATE)
      ? 'adminSubmenu'
      : 'assistantPicker';
  }, [
    isNewSessionListEnabled,
    isTeamAssistant,
    isTeamAdmin,
    canViewOthersSessions,
    hasUpdatedAbility,
  ]);
};

/**
 * Whether to show the "create a session on behalf of another clinician"
 * picker. Thin boolean wrapper over `useNewSessionDelegation` for the upload
 * entry points (`useIsUploadEnabled`, the schedule-upload modal, bulk
 * creation), which don't distinguish the admin submenu from the assistant
 * picker — upload always shows the full picker inside its own modal either way.
 */
export const useShowClinicianSelector = () => useNewSessionDelegation() !== 'none';
